import { FC, memo, useMemo } from "react"
import classNames from "classnames"
import { useSelector } from "react-redux"
import AlertStatusIcon from "./AlertStatusIcon"
import NumberBadge, { NumberBadgeVariant } from "@/components/NumberBadge"
import Button, { ButtonIconPosition } from "@/components/shared/Button"
import { Icon, IconColor } from "@/components/shared/Icon"
import { TestId } from "@/constants/globals"
import { useFastAlarmClearanceEnabled } from "@/hooks/useFastAlarmClearanceEnabled"
import { useTruncation } from "@/hooks/useTruncation"
import { selectTimezone } from "@/store/facilitiesSlice"
import { AlertVariant, AlertVitals, TypefaceVariant } from "@/types/alert"
import { AlertSystemConditionType } from "@/types/alertConfigurations"
import { ButtonVariant, TypefaceColors } from "@/types/index"
import { relationalTime } from "@/utils/alerts"
import { getCellIconName } from "@/utils/cells"
import { renderWithTooltip } from "@/utils/index"
import { isEqual } from "@/utils/lodash"
import "./index.scss"

interface AlertCardProps {
   cellId: string
   // bpm?: number
   // rpm?: number
   isCustom?: boolean
   hasDoorSensor?: boolean
   priority: number
   systemConditionType?: AlertSystemConditionType
   alertVariant: keyof typeof AlertVariant
   typefaceColor: keyof typeof TypefaceVariant
   dateTime: string
   vitals: AlertVitals
   onClick: () => void
   onClearAlert: () => void
}

const AlertCard: FC<AlertCardProps> = ({
   cellId,
   // bpm = 0,
   // rpm = 0,
   isCustom = false,
   hasDoorSensor = false,
   priority,
   systemConditionType,
   alertVariant,
   typefaceColor,
   dateTime,
   vitals,
   onClick,
   onClearAlert,
}) => {
   const fastAlarmClearanceEnabled = useFastAlarmClearanceEnabled()
   const timezone = useSelector(selectTimezone)

   const informativeTime = useMemo(() => {
      return relationalTime(dateTime, timezone)
   }, [dateTime, timezone])

   const [cellIdRef, isCellIdTruncated] = useTruncation<HTMLSpanElement>(cellId)
   const [informativeTimeRef, isInformativeTimeTruncated] =
      useTruncation<HTMLSpanElement>(informativeTime)

   const cellVariantClassName = useMemo(() => {
      return `--${AlertVariant[alertVariant as keyof typeof AlertVariant]}`
   }, [alertVariant])

   const typefaceClassName = useMemo(() => {
      return `alert-card__typeface--${TypefaceVariant[typefaceColor as keyof typeof TypefaceVariant]}`
   }, [typefaceColor])

   const typefaceIconColor = useMemo(
      () =>
         typefaceColor === TypefaceColors.WHITE
            ? IconColor.WHITE
            : IconColor.GUNMETAL,
      [typefaceColor]
   )

   return (
      <div
         className={classNames(
            "alert-card",
            `alert-card${cellVariantClassName}`,
            typefaceClassName,
            { "alert-card--custom": isCustom }
         )}
         onClick={onClick}
         data-testid={TestId.CELL_ALERT_CARD}
      >
         <div className="alert-card__content">
            <div className={classNames("alert-card__header")}>
               <span className="alert-card__cell-state-info">
                  <NumberBadge
                     size={NumberBadgeVariant.SMALL}
                     number={priority}
                     typefaceColor={typefaceColor}
                  />
                  <Icon
                     color={typefaceIconColor}
                     name={getCellIconName(isCustom, hasDoorSensor)}
                     size="16"
                  />
                  {systemConditionType ===
                  AlertSystemConditionType.NO_VITALS ? (
                     <span className="alert-card__status-text">No Vitals</span>
                  ) : (
                     <>
                        <AlertStatusIcon
                           alertKey="bpm"
                           typeface={typefaceIconColor}
                           vitals={{
                              low: vitals.lowHeartRate,
                              high: vitals.highHeartRate,
                           }}
                        />
                        <AlertStatusIcon
                           alertKey="rpm"
                           typeface={typefaceIconColor}
                           vitals={{
                              low: vitals.lowRespiratoryRate,
                              high: vitals.highRespiratoryRate,
                           }}
                        />
                     </>
                  )}
               </span>
               <span className="alert-card__informative-time">
                  {renderWithTooltip(
                     informativeTimeRef,
                     undefined,
                     informativeTime,
                     isInformativeTimeTruncated
                  )}
               </span>
            </div>
            <span className="alert-card__bottom">
               <span className="alert-card__cell-id-wrapper">
                  {renderWithTooltip(
                     cellIdRef,
                     "alert-card__cell-id",
                     cellId,
                     isCellIdTruncated
                  )}
               </span>
            </span>
         </div>
         <div className="alert-card__actions">
            {fastAlarmClearanceEnabled && (
               <Button
                  className="alert-card__clear-alert-button"
                  iconName="clearAlerts"
                  iconSize="12"
                  iconPosition={ButtonIconPosition.LEFT}
                  variant={ButtonVariant.SMALL}
                  onClick={(e) => {
                     e.stopPropagation()
                     e.preventDefault()
                     onClearAlert()
                  }}
               />
            )}
            <Icon
               className="alert-card__arrow-icon"
               name="arrowRight"
               size="12"
               color={typefaceIconColor}
            />
         </div>
      </div>
   )
}

export default memo(AlertCard, (prevProps, nextProps) => {
   const keys: (keyof AlertCardProps)[] = [
      "cellId",
      "isCustom",
      "hasDoorSensor",
      "priority",
      "systemConditionType",
      "alertVariant",
      "typefaceColor",
      "dateTime",
      "vitals",
   ]
   return keys.every((key) => isEqual(prevProps[key], nextProps[key]))
})
